import React, { useState } from 'react';
import { Minus, Plus, ShoppingCart, X } from 'lucide-react';
import type { CartItem } from '../types';
import { CheckoutForm, CheckoutFormData } from './CheckoutForm';

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (id: number, selectedOptions: Record<string, string>, change: number) => void;
  onRemoveItem: (id: number, selectedOptions: Record<string, string>) => void;
}

export function Cart({ items, onUpdateQuantity, onRemoveItem }: CartProps) {
  const [showCheckout, setShowCheckout] = useState(false);
  const total = items.reduce((sum, item) => {
    const itemTotal = (item.price + item.optionsCost) * item.quantity;
    return sum + itemTotal;
  }, 0);
  const deposit = total * 0.5;

  const handleCheckout = (formData: CheckoutFormData) => {
    console.log('Order submitted:', { items, total, deposit, formData });
    setShowCheckout(false);
  };

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <ShoppingCart size={24} />
          <p>Your cart is empty</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <ShoppingCart size={24} />
          Your Cart
        </h2>
        <div className="space-y-4">
          {items.map((item) => {
            const itemTotal = (item.price + item.optionsCost) * item.quantity;
            return (
              <div
                key={`${item.id}-${Object.values(item.selectedOptions).join('-')}`}
                className="flex items-center justify-between py-4 border-b"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div>
                    <h3 className="font-medium">{item.name}</h3>
                    <p className="text-gray-600">
                      £{(item.price + item.optionsCost).toFixed(2)}
                      {item.optionsCost > 0 && (
                        <span className="text-sm text-gray-500">
                          {' '}(Base: £{item.price.toFixed(2)})
                        </span>
                      )}
                    </p>
                    <div className="text-sm text-gray-500 mt-1">
                      {Object.entries(item.selectedOptions).map(([name, value]) => (
                        <span key={name} className="mr-2">
                          {name}: {value}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.selectedOptions, -1)}
                      className="p-1 hover:bg-gray-100 rounded"
                      disabled={item.quantity <= 1}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(item.id, item.selectedOptions, 1)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="text-right min-w-[80px]">
                    <div className="font-medium">£{itemTotal.toFixed(2)}</div>
                  </div>
                  <button
                    onClick={() => onRemoveItem(item.id, item.selectedOptions)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 pt-4 border-t space-y-2">
          <div className="flex justify-between items-center text-lg">
            <span>Total:</span>
            <span>£{total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-lg text-primary font-semibold">
            <span>Required Deposit (50%):</span>
            <span>£{deposit.toFixed(2)}</span>
          </div>
          <button
            onClick={() => setShowCheckout(true)}
            className="w-full mt-4 bg-primary text-white py-3 rounded-md hover:bg-primary/90 transition-colors"
          >
            Pay Deposit & Checkout
          </button>
        </div>
      </div>
      {showCheckout && (
        <CheckoutForm
          items={items}
          total={total}
          deposit={deposit}
          onClose={() => setShowCheckout(false)}
          onSubmit={handleCheckout}
        />
      )}
    </>
  );
}
